import { Sheet, useBreakpointValue, useToast } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import {
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { LinkExternal } from 'src/shared/components';
import { useAiSpaceTypesQuery } from 'src/shared/graphql/generated';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { External } from 'src/shared/route';

import { ScrollableSpaceTypeSelector } from '../../UploadImage/SpaceTypeSelector';
import { SpaceTypeSheetContent } from '../../UploadImage/SpaceTypeSheetContent';
import { useSignUpFromEmbed } from '../hooks/useSignUpFromEmbed';

const LegalText = () => {
  const t = useHomeownerTranslate();
  return (
    <Trans
      components={{
        PrivacyPolicyLink: (
          <LinkExternal color="inherit" href={External.PRIVACY_POLICY.path} />
        ),
        TermsOfUseLink: (
          <LinkExternal color="inherit" href={External.TERMS_OF_SERVICE.path} />
        ),
      }}
      i18nKey="uploadImage.realtorLegalText"
      t={t}
    />
  );
};

export const EmbedUploadSheet = () => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [spaceType, setSpaceType] = useState<string>('');
  const [spaceTypes, setSpaceTypes] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string>('');

  const t = useHomeownerTranslate();
  const isMobile = useBreakpointValue(
    { base: true, tablet: false, desktop: false },
    { fallback: 'tablet' },
  );
  const toast = useToast();
  const { embedOrgId, userEmail } = useEmbedContext();
  const { imageFileToUpload, setImageFileToUpload, loggedIn } =
    useHomeownerAppContext();
  const signUp = useSignUpFromEmbed();

  const handleClose = () => {
    setImageFileToUpload();
  };

  const { data: spaceTypesData } = useAiSpaceTypesQuery({
    variables: { orgId: embedOrgId },
  });

  useEffect(() => {
    setSpaceTypes(spaceTypesData?.aiSpaceTypes?.spaceTypes ?? []);
  }, [spaceTypesData]);

  useEffect(() => {
    const url = imageFileToUpload ? URL.createObjectURL(imageFileToUpload) : '';
    setImageUrl(url);
    return url ? () => URL.revokeObjectURL(url) : () => null;
  }, [imageFileToUpload]);

  const handleNext = async () => {
    if (!loggedIn && userEmail) {
      const signUpResponse = await signUp(userEmail);

      if (signUpResponse.userCreated) {
        // TODO: upload image logic
      } else {
        // TODO: Figure out appropriate error handling
        toast({
          description: t('leadSheet.theresAnIssue'),
          status: 'error',
        });
      }
    }
  };

  const spaceTypeSelectorComponent = isMobile ? (
    <ScrollableSpaceTypeSelector
      setSpaceType={setSpaceType}
      spaceType={spaceType}
      spaceTypes={spaceTypes}
    />
  ) : undefined;

  return (
    <Sheet.Container
      autoFocus={false}
      blockScrollOnMount={false}
      isOpen={!!imageUrl}
      onClose={handleClose}
      variant="bottom"
    >
      <SpaceTypeSheetContent
        agreedToTerms={agreedToTerms}
        canContinue={!spaceType || !agreedToTerms}
        consentMandatory
        ctaText={
          loggedIn
            ? t('uploadImage.createDesigns')
            : t('uploadImage.continueAndCreateAccount')
        }
        imageUrl={imageUrl}
        isLoading={false}
        legalText={<LegalText />}
        onNext={handleNext}
        setAgreedToTerms={setAgreedToTerms}
        setSpaceType={setSpaceType}
        spaceType={spaceType}
        spaceTypeSelectorComponent={spaceTypeSelectorComponent}
        spaceTypes={spaceTypes}
        trackingData={{}}
      />
    </Sheet.Container>
  );
};
